import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../../services/api";
import {
  useSourceMutations,
  type CreateSourcePayload,
  type DiscoveryConfig,
} from "./hooks/useSourceMutations";
import { isValidCronPattern, describeCronPattern } from "./utils/cron-validation";
import { ProjectAutocomplete } from "./ProjectAutocomplete";

interface EditSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceId?: string | null;
}

interface SourceFormData {
  name: string;
  sourceType: string;
  url: string;
  projectId: string;
  checkFrequency: string;
  isActive: boolean;
  articleListSelector: string;
  articleContentSelector: string;
  urlPattern: string;
  endpoint: string;
  extractAsStaticPage: boolean;
  contentExcludeSelectors: string;
}

interface ProjectOperator {
  id: string;
  name: string;
  shortName: string | null;
}

interface ProjectCompetition {
  id: string;
  name: string;
  code: string | null;
}

interface Project {
  id: string;
  name: string;
  operator: ProjectOperator | null;
  competition: ProjectCompetition | null;
}

interface ProjectsResponse {
  projects: Project[];
}

const sourceTypeOptions = [
  { value: "RSS", label: "RSS" },
  { value: "WEBPAGE", label: "Strona WWW" },
  { value: "SITEMAP", label: "Sitemap" },
  { value: "WP_JSON", label: "WordPress API" },
  { value: "STATIC_PAGE", label: "Pojedyncza strona" },
  { value: "JS_RENDER", label: "Strona JS (Playwright)" },
];

const EditSourceModal: React.FC<EditSourceModalProps> = ({
  isOpen,
  onClose,
  sourceId,
}) => {
  const [formData, setFormData] = useState<SourceFormData>({
    name: "",
    sourceType: "RSS",
    url: "",
    projectId: "",
    checkFrequency: "0 */6 * * *",
    isActive: true,
    articleListSelector: "",
    articleContentSelector: "",
    urlPattern: "",
    endpoint: "/wp-json/wp/v2/posts",
    extractAsStaticPage: false,
    contentExcludeSelectors: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [cronValidationError, setCronValidationError] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setFormData({
      name: "",
      sourceType: "RSS",
      url: "",
      projectId: "",
      checkFrequency: "0 */6 * * *",
      isActive: true,
      articleListSelector: "",
      articleContentSelector: "",
      urlPattern: "",
      endpoint: "/wp-json/wp/v2/posts",
      extractAsStaticPage: false,
      contentExcludeSelectors: "",
    });
    setErrors({});
    setGlobalError(null);
    setCronValidationError(null);
  };

  const { submit, isLoading } = useSourceMutations({
    sourceId,
    onSuccess: () => {
      onClose();
      resetForm();
    },
    onFieldErrors: setErrors,
    onGlobalError: setGlobalError,
  });

  // Fetch operator projects for selection
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery<ProjectsResponse>({
    queryKey: ["grants-projects"],
    queryFn: async () => {
      const { data } = await apiClient.get<ProjectsResponse>("/grants/projects");
      return data;
    },
    enabled: isOpen,
  });

  // Fetch source data if editing
  const { data: sourceData, isLoading: isLoadingSource } = useQuery({
    queryKey: ["grants-source", sourceId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/grants/sources/${sourceId}`);
      return data;
    },
    enabled: !!sourceId && isOpen,
  });

  // Populate form when source data is loaded
  useEffect(() => {
    if (sourceData) {
      setFormData({
        name: sourceData.name || "",
        sourceType: sourceData.sourceType || "RSS",
        url: sourceData.url || "",
        projectId: sourceData.project?.id || "",
        checkFrequency: sourceData.checkFrequency || "0 */6 * * *",
        isActive: sourceData.isActive ?? true,
        articleListSelector:
          sourceData.discoveryConfig?.articleListSelector || "",
        articleContentSelector:
          sourceData.discoveryConfig?.articleContentSelector || "",
        urlPattern: sourceData.discoveryConfig?.urlPattern || "",
        endpoint: sourceData.discoveryConfig?.endpoint || "/wp-json/wp/v2/posts",
        extractAsStaticPage: sourceData.discoveryConfig?.extractAsStaticPage ?? false,
        contentExcludeSelectors:
          sourceData.discoveryConfig?.contentExcludeSelectors?.join("\n") ?? "",
      });
    }
  }, [sourceData]);

  const validateCronPattern = (value: string) => {
    if (value && !isValidCronPattern(value)) {
      setCronValidationError("Nieprawidłowy format harmonogramu");
    } else {
      setCronValidationError(null);
    }
  };

  const handleCronChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData({ ...formData, checkFrequency: value });
    // Clear inline error while typing (will re-validate on blur)
    if (cronValidationError) {
      setCronValidationError(null);
    }
  };

  const handleCronBlur = () => {
    validateCronPattern(formData.checkFrequency);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setGlobalError(null);

    // Validate and parse contentExcludeSelectors
    const excludeLines = formData.contentExcludeSelectors
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (excludeLines.length > 10) {
      setErrors((prev) => ({
        ...prev,
        contentExcludeSelectors: "Maksymalnie 10 selektorów.",
      }));
      return;
    }
    const longLine = excludeLines.find((s) => s.length > 500);
    if (longLine) {
      setErrors((prev) => ({
        ...prev,
        contentExcludeSelectors: "Każdy selektor może mieć maksymalnie 500 znaków.",
      }));
      return;
    }

    // Build discovery config based on source type
    const discoveryConfig: DiscoveryConfig = {};
    if (formData.sourceType === "WEBPAGE" || formData.sourceType === "JS_RENDER") {
      discoveryConfig.articleListSelector = formData.articleListSelector;
      discoveryConfig.articleContentSelector = formData.articleContentSelector;
      if (formData.sourceType === "JS_RENDER" && formData.extractAsStaticPage) {
        discoveryConfig.extractAsStaticPage = true;
      }
    } else if (formData.sourceType === "SITEMAP") {
      discoveryConfig.urlPattern = formData.urlPattern;
    } else if (formData.sourceType === "WP_JSON") {
      discoveryConfig.endpoint = formData.endpoint;
    }
    if (excludeLines.length > 0) {
      discoveryConfig.contentExcludeSelectors = excludeLines;
    }

    const payload: CreateSourcePayload = {
      name: formData.name,
      sourceType: formData.sourceType,
      url: formData.url,
      projectId: formData.projectId || null,
      checkFrequency: formData.checkFrequency,
      isActive: formData.isActive,
      discoveryConfig: Object.keys(discoveryConfig).length > 0 ? discoveryConfig : null,
    };

    submit(payload);
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        resetForm();
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, isLoading]);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen && !isLoadingSource) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, isLoadingSource]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-medium text-neutral-900">
            {sourceId ? "Edytuj źródło" : "Dodaj nowe źródło"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {globalError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
              <p className="text-sm text-red-700">{globalError}</p>
            </div>
          )}

          {isLoadingSource ? (
            <div className="text-center py-8" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">Ładowanie...</p>
            </div>
          ) : (
            <>
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Nazwa <span className="text-red-600">*</span>
                </label>
                <input
                  ref={firstInputRef}
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.name ? "border-red-500" : "border-neutral-300"
                  }`}
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Source Type */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Typ źródła <span className="text-red-600">*</span>
                </label>
                <select
                  value={formData.sourceType}
                  onChange={(e) =>
                    setFormData({ ...formData, sourceType: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                  required
                >
                  {sourceTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* URL */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  URL <span className="text-red-600">*</span>
                </label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) =>
                    setFormData({ ...formData, url: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    errors.url ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="https://example.com/feed"
                  required
                />
                {errors.url && (
                  <p className="mt-1 text-xs text-red-600">{errors.url}</p>
                )}
              </div>

              {/* Conditional Fields */}
              {(formData.sourceType === "WEBPAGE" || formData.sourceType === "JS_RENDER") && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Selector listy artykułów (CSS){" "}
                      {formData.sourceType === "JS_RENDER" && <span className="text-red-600">*</span>}
                    </label>
                    <input
                      type="text"
                      value={formData.articleListSelector}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          articleListSelector: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder=".article-list a"
                      required={formData.sourceType === "JS_RENDER"}
                    />
                    <p className="mt-1 text-xs text-neutral-500">
                      {formData.extractAsStaticPage
                        ? "CSS selector obszaru treści strony"
                        : "CSS selector do znalezienia linków do artykułów"}
                      {formData.sourceType === "JS_RENDER" && " (wymagany dla JS_RENDER)"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Selector treści artykułu (CSS)
                    </label>
                    <input
                      type="text"
                      value={formData.articleContentSelector}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          articleContentSelector: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                      placeholder=".article-content"
                    />
                    <p className="mt-1 text-xs text-neutral-500">
                      CSS selector do pobrania treści artykułu
                    </p>
                  </div>
                </>
              )}

              {formData.sourceType === "JS_RENDER" && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="extractAsStaticPage"
                    checked={formData.extractAsStaticPage}
                    onChange={(e) =>
                      setFormData({ ...formData, extractAsStaticPage: e.target.checked })
                    }
                    className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                  />
                  <label
                    htmlFor="extractAsStaticPage"
                    className="ml-2 text-sm text-neutral-700"
                  >
                    Traktuj jako pojedynczą stronę (bez linków)
                  </label>
                  <p className="ml-2 text-xs text-neutral-500">
                    - dla stron SPA bez tradycyjnych linków
                  </p>
                </div>
              )}

              {formData.sourceType === "SITEMAP" && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Wzorzec URL (regex)
                  </label>
                  <input
                    type="text"
                    value={formData.urlPattern}
                    onChange={(e) =>
                      setFormData({ ...formData, urlPattern: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    placeholder="/aktualnosci/.*"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    Wzorzec regex do filtrowania URL-i z sitemapy
                  </p>
                </div>
              )}

              {formData.sourceType === "WP_JSON" && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Endpoint API
                  </label>
                  <input
                    type="text"
                    value={formData.endpoint}
                    onChange={(e) =>
                      setFormData({ ...formData, endpoint: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500"
                    placeholder="/wp-json/wp/v2/posts"
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    Ścieżka do WordPress REST API endpoint
                  </p>
                </div>
              )}

              {/* Operator project (optional) */}
              <div>
                <label
                  id="projectId-label"
                  htmlFor="projectId-trigger"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Projekt operatora (opcjonalnie)
                </label>
                <ProjectAutocomplete
                  projects={projectsData?.projects ?? []}
                  value={formData.projectId}
                  onChange={(projectId) =>
                    setFormData({ ...formData, projectId })
                  }
                  disabled={isLoadingProjects}
                  isLoading={isLoadingProjects}
                  triggerId="projectId-trigger"
                  ariaLabelledBy="projectId-label"
                />
              </div>

              {/* Check Frequency */}
              <div>
                <label
                  htmlFor="checkFrequency"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Częstotliwość sprawdzania (cron)
                </label>
                <input
                  id="checkFrequency"
                  type="text"
                  value={formData.checkFrequency}
                  onChange={handleCronChange}
                  onBlur={handleCronBlur}
                  aria-describedby="checkFrequency-help checkFrequency-error"
                  aria-invalid={!!(errors.checkFrequency || cronValidationError)}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 font-mono text-sm ${
                    errors.checkFrequency || cronValidationError ? "border-red-500" : "border-neutral-300"
                  }`}
                  placeholder="0 */6 * * *"
                />
                {(errors.checkFrequency || cronValidationError) && (
                  <p id="checkFrequency-error" className="mt-1 text-xs text-red-600" role="alert">
                    {errors.checkFrequency || cronValidationError}
                  </p>
                )}
                {describeCronPattern(formData.checkFrequency) && (
                  <p className="mt-1 text-xs text-green-600 font-medium">
                    {describeCronPattern(formData.checkFrequency)}
                  </p>
                )}
                <p id="checkFrequency-help" className="mt-1 text-xs text-neutral-500">
                  Format: minuta godzina dzień miesiąc dzień_tygodnia.{" "}
                  Przykłady: <code className="bg-neutral-100 px-1 rounded">0 */6 * * *</code> (co 6h),{" "}
                  <code className="bg-neutral-100 px-1 rounded">0 9 * * 1-5</code> (dni robocze 9:00).{" "}
                  <a
                    href="https://crontab.guru/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Generator cron
                  </a>
                </p>
              </div>

              {/* Content Exclude Selectors */}
              <div>
                <label
                  htmlFor="contentExcludeSelectors"
                  className="block text-sm font-medium text-neutral-700 mb-1"
                >
                  Selektory CSS do wykluczenia (opcjonalne)
                </label>
                <textarea
                  id="contentExcludeSelectors"
                  value={formData.contentExcludeSelectors}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contentExcludeSelectors: e.target.value,
                    })
                  }
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 font-mono text-sm ${
                    errors.contentExcludeSelectors
                      ? "border-red-500"
                      : "border-neutral-300"
                  }`}
                  placeholder=".view-count&#10;.metadata-date&#10;.share-buttons"
                />
                {errors.contentExcludeSelectors && (
                  <p className="mt-1 text-xs text-red-600" role="alert">
                    {errors.contentExcludeSelectors}
                  </p>
                )}
                <div className="mt-1 flex items-start justify-between gap-2">
                  <p className="text-xs text-neutral-500">
                    Elementy CSS usuwane przed obliczeniem content hash. Jeden selektor na linię. Max 10, max 500 znaków każdy. Dla RSS i WP_JSON ignorowane.
                  </p>
                  <span className="text-xs text-neutral-400 whitespace-nowrap">
                    {formData.contentExcludeSelectors
                      .split("\n")
                      .filter((s) => s.trim().length > 0).length}
                    /10
                  </span>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="h-4 w-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                />
                <label
                  htmlFor="isActive"
                  className="ml-2 text-sm text-neutral-700"
                >
                  Źródło aktywne
                </label>
              </div>
            </>
          )}
        </form>

        <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isLoadingSource}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading && (
              <div role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span className="sr-only">Zapisywanie...</span>
              </div>
            )}
            {isLoading
              ? "Zapisywanie..."
              : sourceId
              ? "Zaktualizuj"
              : "Dodaj źródło"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSourceModal;
